import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that intercepts clicks on the model-picker links and
 * swaps the page content in place via fetch + morph, instead of a full
 * page reload. Progressive enhancement: without JS the `<a href>` still
 * navigates normally.
 *
 * @extends Component
 */
class ModelPicker extends Component {
  #selectors = {
    link: 'a[data-model-url]',
  };

  #classes = {
    loading: 'is-loading',
  };

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Cache of prefetched HTML responses keyed by URL. Populated on hover,
   * consumed on click so the morph doesn't wait on a fresh network request.
   *
   * @type {Map<string, Promise<string>>}
   */
  #prefetchCache = new Map();

  /**
   * Last pathname+search we navigated to. Used to distinguish real back/forward
   * from hash-only changes (e.g. clicking an `<a href="#anchor">` which fires
   * popstate in Chrome but should not re-fetch the page).
   *
   * @type {string}
   */
  #lastPath = '';

  /**
   * Lifecycle — component connected to DOM.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#lastPath = window.location.pathname + window.location.search;
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerover', this.#handlePointerOver);
    window.addEventListener('popstate', this.#handlePopstate);
  }

  /**
   * Lifecycle — component removed from DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerover', this.#handlePointerOver);
    window.removeEventListener('popstate', this.#handlePopstate);
    this.#abortController?.abort();
    this.#prefetchCache.clear();
  }

  /**
   * Handle a click on any descendant. Intercepts only real left-clicks on
   * `<a[data-model-url]>` without modifier keys.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = /** @type {HTMLElement | null} */ (event.target);
    const link = target?.closest?.(this.#selectors.link);
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!this.contains(link)) return;

    const url = link.dataset.modelUrl;
    if (!url) return;

    // Let Shopify theme editor handle links natively (no JS interception).
    if (window.Shopify?.designMode) return;

    event.preventDefault();
    this.#navigateTo(url, { pushState: true });
  };

  /**
   * Warm the HTTP cache for the hovered variant so a subsequent click can
   * reuse the response and morph instantly. Mouse-only — touch devices skip
   * this to avoid wasting data on accidental hovers.
   *
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const connection = /** @type {{ saveData?: boolean } | undefined} */ (
      /** @type {any} */ (navigator).connection
    );
    if (connection?.saveData) return;

    const target = /** @type {HTMLElement | null} */ (event.target);
    const link = target?.closest?.(this.#selectors.link);
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!this.contains(link)) return;

    const url = link.dataset.modelUrl;
    if (!url) return;
    if (this.#prefetchCache.has(url)) return;

    const target_path = new URL(url, window.location.origin).pathname;
    if (target_path === window.location.pathname) return;

    this.#prefetchCache.set(url, this.#prefetch(url));
  };

  /**
   * Fetch a target URL at low priority and return its HTML body. Failures
   * are dropped from the cache so a real click can retry.
   *
   * @param {string} url
   * @returns {Promise<string>}
   */
  #prefetch = async (url) => {
    try {
      const response = await fetch(url, {
        // @ts-ignore — Fetch Priority API not in lib.dom yet.
        priority: 'low',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.text();
    } catch (error) {
      this.#prefetchCache.delete(url);
      throw error;
    }
  };

  /**
   * Handle browser back/forward. Re-fetch + morph to the restored URL.
   * Ignores hash-only changes (e.g. in-page anchor navigation) which fire
   * popstate in some browsers but shouldn't trigger a re-fetch.
   *
   * @param {PopStateEvent} _event
   */
  #handlePopstate = (_event) => {
    const path = window.location.pathname + window.location.search;
    if (path === this.#lastPath) return;
    this.#lastPath = path;
    this.#navigateTo(window.location.href, { pushState: false });
  };

  /**
   * Promote `lazy-src`/`lazy-srcset` to real `src`/`srcset` on above-the-fold
   * images in the parsed response, then preload them so they hit the browser
   * cache before morph — avoids the visible loading flash on model switch.
   *
   * Below-the-fold images keep their `lazy-*` attributes and are handled by
   * the observer-CFH after morph (via `reactivateLazyObserver`).
   *
   * @param {Element} newMain - The new `<main>` element from the fetched HTML.
   * @returns {Promise<void>}
   */
  #preloadEagerImages = async (newMain) => {
    const eagerImages = /** @type {HTMLImageElement[]} */ (
      Array.from(
        newMain.querySelectorAll(
          'img[loading="eager"], .model-picker__image[lazy-src], .cross-sell-block__image[lazy-src], .band-upsell-block__image[lazy-src]'
        )
      )
    );

    /** @type {Promise<void>[]} */
    const preloads = [];

    for (const img of eagerImages) {
      const lazySrc = img.getAttribute('lazy-src');
      const lazySrcset = img.getAttribute('lazy-srcset');

      if (lazySrc) {
        img.setAttribute('src', lazySrc);
        img.removeAttribute('lazy-src');
      }
      if (lazySrcset) {
        img.setAttribute('srcset', lazySrcset);
        img.removeAttribute('lazy-srcset');
      }

      // Promote lazy <source> siblings inside a <picture> too.
      const picture = img.closest('picture');
      if (picture) {
        for (const source of picture.querySelectorAll('source[lazy-srcset]')) {
          const lazy = source.getAttribute('lazy-srcset');
          if (lazy) {
            source.setAttribute('srcset', lazy);
            source.removeAttribute('lazy-srcset');
          }
        }
      }

      const src = img.getAttribute('src');
      if (!src) continue;

      preloads.push(
        new Promise((resolve) => {
          const loader = new Image();
          const srcset = img.getAttribute('srcset');
          const sizes = img.getAttribute('sizes');
          if (sizes) loader.sizes = sizes;
          if (srcset) loader.srcset = srcset;
          loader.src = src;
          if (loader.complete) {
            resolve();
          } else {
            loader.onload = () => resolve();
            loader.onerror = () => resolve();
          }
        })
      );
    }

    // Cap the wait so a slow image doesn't freeze the UX.
    await Promise.race([
      Promise.all(preloads),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  };

  /**
   * Re-sync `<template>` contents after a morph (morph ignores `.content`),
   * paired by closest data-media-id. See .claude/rules/pdp-morph.md.
   * @param {Element} main - Live (morphed) `<main>`.
   * @param {Element} newMain - Freshly parsed `<main>` from the fetch.
   */
  #refreshTemplates = (main, newMain) => {
    /** @type {Map<string, string>} */
    const freshByMediaId = new Map();
    for (const tpl of newMain.querySelectorAll('template')) {
      const mediaId = tpl.closest('[data-media-id]')?.getAttribute('data-media-id');
      if (mediaId) freshByMediaId.set(mediaId, tpl.innerHTML);
    }
    if (!freshByMediaId.size) return;

    for (const tpl of main.querySelectorAll('template')) {
      const mediaId = tpl.closest('[data-media-id]')?.getAttribute('data-media-id');
      if (!mediaId) continue;
      const fresh = freshByMediaId.get(mediaId);
      if (fresh != null && tpl.innerHTML !== fresh) tpl.innerHTML = fresh;
    }
  };

  /**
   * Fetch the target URL, morph `<main>` in place, and update history.
   *
   * @param {string} url - The product URL to navigate to.
   * @param {{ pushState: boolean }} options
   */
  #navigateTo = async (url, { pushState }) => {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.classList.add(this.#classes.loading);

    try {
      let responseText;
      const prefetched = this.#prefetchCache.get(url);
      if (prefetched) {
        responseText = await prefetched;
      } else {
        const response = await fetch(url, { signal: this.#abortController.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        responseText = await response.text();
      }

      const newHtml = new DOMParser().parseFromString(responseText, 'text/html');

      const main = document.querySelector('main');
      const newMain = newHtml.querySelector('main');
      if (!main || !newMain) throw new Error('No main element found');

      // Popstate navigated back past the product page (e.g. collection).
      // Don't morph a non-product layout — let the browser reload properly.
      if (!pushState && !newMain.querySelector('model-picker')) {
        window.location.reload();
        return;
      }

      // Preload above-the-fold images so the morph doesn't show a loading flash.
      await this.#preloadEagerImages(newMain);

      morph(main, newMain, MORPH_OPTIONS);
      this.#refreshTemplates(main, newMain);

      if (pushState) {
        history.pushState({}, '', url);
      }

      const navigated = new URL(url, window.location.origin);
      this.#lastPath = navigated.pathname + navigated.search;

      document.dispatchEvent(
        new CustomEvent('reactivateLazyObserver', { detail: { lazyParentElement: document.body } })
      );

      document.dispatchEvent(new CustomEvent('model:update', { detail: { url }, bubbles: true }));
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('ModelPicker navigation failed:', error);
      window.location.href = url;
    } finally {
      this.classList.remove(this.#classes.loading);
    }
  };
}

if (!customElements.get('model-picker')) {
  customElements.define('model-picker', ModelPicker);
}
